interface Props {
  onBack: () => void;
}

const policyCommand = 'powershell.exe -NoProfile -ExecutionPolicy Bypass -File "$env:USERPROFILE/Downloads/enable-webusb-v2-policy.ps1" -Browser Both';

export function WindowsDriverGuidePage({ onBack }: Props) {
  return <div className="app-shell driver-guide-shell">
    <header className="topbar driver-guide-topbar">
      <button type="button" className="brand-mark brand-button" onClick={onBack} aria-label="Quay lại USB Device Workbench">
        <span>U</span>
        <div className="brand-button-copy"><strong>USB Device Workbench</strong><small>Hướng dẫn driver Windows</small></div>
      </button>
      <button type="button" className="secondary" onClick={onBack}>← Quay lại công cụ</button>
    </header>

    <main className="driver-guide" aria-labelledby="driver-guide-title">
      <section className="driver-guide-hero">
        <p className="eyebrow">WINDOWS · WEBUSB</p>
        <h1 id="driver-guide-title">Cài driver cho ADB, Fastboot và Amlogic</h1>
        <p>Hướng dẫn này dành cho Chrome/Edge trên Windows 10/11. Mỗi chế độ USB có thể xuất hiện thành một thiết bị khác, vì vậy cần kiểm tra lại driver sau khi chuyển từ Android sang Fastboot, WorldCup hoặc ADNL.</p>
        <nav className="driver-guide-jumps" aria-label="Mục lục hướng dẫn">
          <a href="#adb-fastboot">ADB / Fastboot</a>
          <a href="#amlogic">Amlogic</a>
          <a href="#zadig-v2">Zadig cho V2</a>
          <a href="#v2-policy">Policy WebUSB V2</a>
          <a href="#troubleshooting">Xử lý lỗi</a>
        </nav>
      </section>

      <aside className="driver-guide-alert" role="note">
        <strong>Không bind theo tên thiết bị nếu chưa kiểm tra VID/PID.</strong>
        <span>Zadig có thể thay driver của bàn phím, chuột hoặc ổ lưu trữ nếu chọn nhầm. Với Amlogic V2 của công cụ này, chỉ thao tác trên thiết bị có <code>VID 1B8E</code> và <code>PID C003</code>.</span>
      </aside>

      <section id="adb-fastboot" className="panel driver-guide-section">
        <div className="driver-guide-heading"><span>01</span><div><p className="eyebrow">ANDROID</p><h2>Driver ADB và Fastboot</h2></div></div>
        <div className="driver-guide-columns">
          <div>
            <h3>ADB</h3>
            <ol>
              <li>Bật <b>Developer options</b> và <b>USB debugging</b> trên thiết bị.</li>
              <li>Cài driver USB do hãng thiết bị cung cấp. Với thiết bị Google, có thể dùng <a href="https://developer.android.com/studio/run/win-usb" target="_blank" rel="noreferrer">Google USB Driver</a>.</li>
              <li>Mở Device Manager và kiểm tra thiết bị xuất hiện dưới nhóm Android/ADB, không có dấu chấm than vàng.</li>
              <li>Mở khóa màn hình, kết nối ở tab ADB và chấp nhận hộp thoại RSA trên thiết bị.</li>
            </ol>
          </div>
          <div>
            <h3>Fastboot / Fastbootd</h3>
            <ol>
              <li>Khởi động thiết bị vào bootloader hoặc fastbootd rồi mở lại Device Manager.</li>
              <li>Nếu Windows tạo entry mới, cài Android Bootloader Interface từ driver OEM/Google.</li>
              <li>Nếu trình duyệt vẫn báo access denied, chỉ bind đúng entry Fastboot sang <b>WinUSB</b>; không thay driver của ADB đang hoạt động.</li>
              <li>Rút/cắm lại cáp và dùng nút kết nối trong tab Fastboot.</li>
            </ol>
          </div>
        </div>
        <p className="driver-guide-note">ADB và Fastboot dùng PID/interface khác nhau. ADB chạy được không có nghĩa Fastboot đã có driver đúng.</p>
      </section>

      <section id="amlogic" className="panel driver-guide-section">
        <div className="driver-guide-heading"><span>02</span><div><p className="eyebrow">AMLOGIC USB</p><h2>Driver Amlogic V3 và V2</h2></div></div>
        <div className="driver-guide-table-wrap">
          <table className="driver-guide-table">
            <thead><tr><th>Chế độ</th><th>Nhận diện</th><th>Driver dùng với trình duyệt</th><th>Lưu ý</th></tr></thead>
            <tbody>
              <tr><td><b>V3 · ADNL</b></td><td>Amlogic ADNL/loader; thường VID <code>1B8E</code></td><td>WinUSB trên đúng interface</td><td>Thiết bị có thể đổi PID khi nạp BL2/U-Boot; Windows phải nhận đúng các stage xuất hiện.</td></tr>
              <tr><td><b>V2 · WorldCup</b></td><td>VID <code>1B8E</code> / PID <code>C003</code></td><td>WinUSB + policy WebUSB bên dưới</td><td>WorldCup/libusb-win32 của tool native không tương thích với Chrome WebUSB.</td></tr>
            </tbody>
          </table>
        </div>
        <ol>
          <li>Tắt Amlogic USB Burning Tool, USB_Burning_Tool và mọi ứng dụng đang giữ thiết bị.</li>
          <li>Đưa box vào chế độ USB burn, sau đó xác nhận VID/PID trong Device Manager: Properties → Details → Hardware Ids.</li>
          <li>Nếu driver hiện tại đã là WinUSB, không cần chạy Zadig lại. Nếu là WorldCup/libusb, làm theo phần tiếp theo.</li>
        </ol>
        <div className="driver-download-card">
          <div>
            <p className="eyebrow">BỘ CÀI ĐI KÈM</p>
            <h3>Driver Amlogic cho Windows</h3>
            <p>Tải gói driver đã chuẩn bị sẵn để cài cho Amlogic V2/V3. Sau khi giải nén, chạy bộ cài bằng quyền Administrator và chỉ chọn đúng thiết bị Amlogic.</p>
          </div>
          <a className="download-button" href="/Driver.zip" download="Amlogic-Driver.zip">Tải Driver.zip</a>
        </div>
      </section>

      <section id="zadig-v2" className="panel driver-guide-section">
        <div className="driver-guide-heading"><span>03</span><div><p className="eyebrow">AMLOGIC V2 · WINDOWS</p><h2>Bind WinUSB bằng Zadig</h2></div></div>
        <aside className="driver-guide-warning"><b>Ảnh hưởng tới tool native:</b> sau khi thay bằng WinUSB, Amlogic USB Burning Tool V2 có thể không nhận box. Muốn dùng lại tool native, hãy cài lại WorldCup driver bằng bộ cài Amlogic hoặc Device Manager.</aside>
        <div className="driver-download-card v2-driver-download">
          <div>
            <p className="eyebrow">AMLOGIC V2 · WORLDCUP</p>
            <h3>WorldCup Device Driver</h3>
            <p>Bộ driver riêng cho Amlogic V2/WorldCup. Giải nén rồi chạy bộ cài bằng quyền Administrator trước khi bind WinUSB bằng Zadig.</p>
          </div>
          <a className="download-button" href="/WorldCup_Device.zip" download="WorldCup_Device.zip">Tải WorldCup_Device.zip</a>
        </div>
        <ol className="driver-guide-steps">
          <li><span>1</span><div>Tải Zadig từ <a href="https://zadig.akeo.ie/" target="_blank" rel="noreferrer">trang chính thức zadig.akeo.ie</a>, sau đó chạy bằng quyền Administrator.</div></li>
          <li><span>2</span><div>Kết nối duy nhất box V2 cần bind và đưa box vào chế độ WorldCup/USB burn.</div></li>
          <li><span>3</span><div>Trong Zadig chọn <b>Options → List All Devices</b>.</div></li>
          <li><span>4</span><div>Chọn entry Amlogic/WorldCup và kiểm tra ô USB ID phải là <code>1B8E : C003</code>. Nếu không đúng, dừng lại.</div></li>
          <li><span>5</span><div>Ở ô driver đích chọn <b>WinUSB</b>, rồi bấm <b>Replace Driver</b> hoặc <b>Install Driver</b>.</div></li>
          <li><span>6</span><div>Rút/cắm lại box. Trong Device Manager, driver provider có thể khác nhau nhưng service/driver phải là <code>WinUSB</code>.</div></li>
        </ol>
      </section>

      <section id="v2-policy" className="panel driver-guide-section policy-section">
        <div className="driver-guide-heading"><span>04</span><div><p className="eyebrow">CHROME / EDGE</p><h2>Cài policy WebUSB cho Amlogic V2</h2></div></div>
        <p>Policy cho phép Chrome/Edge giữ quyền với WorldCup V2 qua các lần USB reset/re-enumerate. Policy không thay driver; phải hoàn tất bước Zadig trước.</p>
        <a className="download-button" href="/enable-webusb-v2-policy.ps1" download="enable-webusb-v2-policy.ps1">Tải enable-webusb-v2-policy.ps1</a>
        <ol>
          <li>Lưu file vào một thư mục dễ tìm, ví dụ <code>Downloads</code>.</li>
          <li>Mở PowerShell tại thư mục chứa file.</li>
          <li>Chạy lệnh sau. Script sẽ yêu cầu UAC để ghi policy cho Chrome và Edge:</li>
        </ol>
        <pre className="driver-guide-command"><code>{policyCommand}</code></pre>
        <p>Nếu chỉ dùng một trình duyệt, đổi <code>Both</code> thành <code>Chrome</code> hoặc <code>Edge</code>. Khi website chạy ở domain/IP khác mặc định trong script, truyền origin chính xác:</p>
        <pre className="driver-guide-command"><code>powershell -NoProfile -ExecutionPolicy Bypass -File "$env:USERPROFILE/Downloads/enable-webusb-v2-policy.ps1" -Browser Both -Origins "https://ten-mien-cua-ban"</code></pre>
        <ol start={4}>
          <li>Mở <code>chrome://policy</code> hoặc <code>edge://policy</code>, bấm <b>Reload policies</b>.</li>
          <li>Kiểm tra policy <code>WebUsbAllowDevicesForUrls</code> xuất hiện, sau đó đóng hoàn toàn và mở lại trình duyệt.</li>
          <li>Quay lại tab Amlogic V2, bấm <b>Thêm thiết bị</b> và chọn WorldCup V2.</li>
        </ol>
        <details className="driver-guide-details"><summary>Gỡ policy</summary><pre className="driver-guide-command"><code>{policyCommand} -Remove</code></pre></details>
      </section>

      <section id="troubleshooting" className="panel driver-guide-section">
        <div className="driver-guide-heading"><span>05</span><div><p className="eyebrow">KIỂM TRA</p><h2>Xử lý lỗi thường gặp</h2></div></div>
        <dl className="driver-guide-faq">
          <div><dt>Chrome báo Access denied</dt><dd>Đóng tool Amlogic native, kiểm tra đúng entry đang dùng WinUSB, rút/cắm lại thiết bị và khởi động lại trình duyệt.</dd></div>
          <div><dt>V2 mất kết nối sau reset</dt><dd>Kiểm tra policy đã được tải trong <code>chrome://policy</code>/<code>edge://policy</code>, origin trong policy trùng chính xác với địa chỉ đang mở và driver của PID tái xuất hiện vẫn là WinUSB.</dd></div>
          <div><dt>Zadig không thấy thiết bị</dt><dd>Bật Options → List All Devices, đổi cáp/cổng USB, đưa box lại vào burn mode và kiểm tra Device Manager có Hardware ID <code>VID_1B8E&amp;PID_C003</code>.</dd></div>
          <div><dt>USB Burning Tool V2 không còn nhận box</dt><dd>Đây là kết quả dự kiến sau khi bind WinUSB. Gỡ thiết bị kèm driver trong Device Manager rồi cài lại WorldCup driver từ bộ Amlogic native.</dd></div>
          <div><dt>ADB có thiết bị nhưng web không kết nối</dt><dd>Mở khóa Android, bật USB debugging, chấp nhận RSA; nếu không có hộp thoại, thu hồi USB debugging authorizations rồi thử lại.</dd></div>
        </dl>
      </section>
    </main>

    <footer>
      <div className="footer-meta"><span>Chỉ bind WinUSB cho đúng thiết bị đã xác minh VID/PID</span><span>Windows 10/11 · Chrome/Edge desktop</span></div>
      <div className="footer-actions"><button type="button" className="secondary" onClick={onBack}>Quay lại công cụ</button></div>
    </footer>
  </div>;
}
